import { Children, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';

function FormActions({
  cancelHref,
  submitLabel,
  pending,
  stretch,
}: {
  cancelHref: string;
  submitLabel: string;
  pending: boolean;
  stretch: boolean;
}) {
  const buttonClass = cn('min-h-11', stretch && 'flex-1');
  return (
    <>
      <Button variant="outline" className={buttonClass} asChild>
        <Link to={cancelHref}>Abbrechen</Link>
      </Button>
      <Button type="submit" className={buttonClass} disabled={pending}>
        {submitLabel}
      </Button>
    </>
  );
}

/**
 * Shared create/edit shell. On the phone the actions stick above the platform
 * bottom nav (that nav already owns the safe area). From `sm` they sit in the
 * last card.
 */
export function MaintenanceFormPage({
  title,
  description,
  cancelHref,
  submitLabel = 'Speichern',
  pending = false,
  pendingLabel = 'Wird gespeichert …',
  onSubmit,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  cancelHref: string;
  submitLabel?: string;
  pending?: boolean;
  pendingLabel?: string;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
}) {
  const sections = Children.toArray(children);
  const label = pending ? pendingLabel : submitLabel;

  return (
    <form
      className="space-y-6 pb-20 sm:pb-0"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(event);
      }}
    >
      <PlatformPageHeader title={title} description={description} />
      <div className="space-y-4">
        {(sections.length > 0 ? sections : [null]).map((section, index, all) => {
          const last = index === all.length - 1;
          return (
            <Card key={index}>
              {section ? <CardContent>{section}</CardContent> : null}
              {last ? (
                <CardFooter className="hidden justify-end gap-2 border-t sm:flex">
                  <FormActions
                    cancelHref={cancelHref}
                    submitLabel={label}
                    pending={pending}
                    stretch={false}
                  />
                </CardFooter>
              ) : null}
            </Card>
          );
        })}
      </div>
      <div className="fixed inset-x-0 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-30 border-t bg-background px-4 py-3 sm:hidden">
        <div className="mx-auto flex w-full max-w-7xl gap-2">
          <FormActions cancelHref={cancelHref} submitLabel={label} pending={pending} stretch />
        </div>
      </div>
    </form>
  );
}
